import './index.scss';
import React from 'react';
import RSVP_CONSTS from '../../../constants';
import {
    GECKO_CONSTS,
    GeckoBtn,
    GeckoRow,
    GeckoCol
} from '@geckolabs/gecko-react-ui';

// Services
import RsvpService from '../../../services/RsvpService';
import DateUtils from '../../../services/DateUtils';
import { __, STR_KEY } from '../../../services/Translate';
import moment from 'moment';

const checkinTimeString = (event, currentTime, startTime) => {
    let checkinTime = moment(event.start_datetime).subtract(event.checkin_start || 0, 'minutes').format('YYYY-MM-DD HH:mm:ss');
    if (currentTime > (startTime - 86400 - (event.checkin_start * 60))) {
        return DateUtils.hourMinute(checkinTime);
    }
    return DateUtils.dayMonthYearHourMinute(checkinTime);
};

const renderBtn = (name, icon, type, handler) => {
    return (
        <div className="row eventCheckIn status-control-btn">
            <GeckoCol width="12">
                <div onClick={(evt) => evt.stopPropagation()}>
                    <GeckoBtn {...{name, icon, type, handler}} />
                </div>
            </GeckoCol>
        </div>
    );
};

const renderAlert = (text, type, icon) => {
    return (
        <GeckoRow>
            <GeckoCol width="12">
               <div className={`status-control-alert alert alert-${type || 'info'}`}>
                    {icon ? (<i className={`far fa-${icon}`}></i>) : null}
                    <span>{text}</span>
                </div>
            </GeckoCol>
        </GeckoRow>
    );
};

const StatusControl = ({ event, checkinHandler, isSession }) => {
    if (!event) return null;

    const currentTime           = DateUtils.utcNow();
    const startTime             = event.start_datetime_utc;
    const eventIsFull           = event.full && (!event.auto_waitlist || event.full_waitlist);
    const eventHasPassed        = RsvpService.hasEventPassed(event);
    const eventHasClosed        = RsvpService.hasEventClosed(event);
    const onlineSingleEvent     = RsvpService.isOnline(event) && RsvpService.isSingleEvent(event);
    const onlineSessionEvent    = RsvpService.isOnline(event) && !RsvpService.isSingleEvent(event);
    const venueEventWithCheckIn = !RsvpService.isOnline(event) && event.checkin_start !== null;
    const isCheckinEvent        = onlineSingleEvent || onlineSessionEvent || venueEventWithCheckIn;
    const checkinHasStarted     = event.checkin_started;
    const eventTimezone         = event.timezone;

    const statusIsInvited       = RSVP_CONSTS.ATTENDANCE.STATUS_INVITED === event.attendance.status;
    const statusIsWaitlisted    = RSVP_CONSTS.ATTENDANCE.STATUS_WAITLISTED === event.attendance.status;
    const statusIsCancelled     = RSVP_CONSTS.ATTENDANCE.STATUS_CANCELLED === event.attendance.status;
    const statusIsDidNotAttend  = RSVP_CONSTS.ATTENDANCE.STATUS_DID_NOT_ATTEND === event.attendance.status;
    const statusIsRemoved       = RSVP_CONSTS.ATTENDANCE.STATUS_REMOVED === event.attendance.status;
    const statusCanAttend       = [RSVP_CONSTS.ATTENDANCE.STATUS_REGISTERED, RSVP_CONSTS.ATTENDANCE.STATUS_INVITED].indexOf(event.attendance.status) !== -1;
    const statusIsAttending     = [RSVP_CONSTS.ATTENDANCE.STATUS_ATTENDED, RSVP_CONSTS.ATTENDANCE.STATUS_ENGAGED].indexOf(event.attendance.status) !== -1;

    let showAlert = false, alertText, alertType = GECKO_CONSTS.ALERT_TYPE_INFO, alertIcon = 'info-circle';
    
    /**
     * Show alert when the event:
     * - has passed
     * OR
     * - registration is closed AND attendance status is INVITED
     * OR
     * - attendance status is WAITLISTED, CANCELLED, DID_NOT_ATTEND, REMOVED
     *
     *
     * TBD - OR is Full (with waitlisting disabled) AND attendance status is INVITED, WAITLISTED
     */

    if (eventHasPassed) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_ENDED : STR_KEY.PROFILE_ALERT_ENDED);
    } else if (statusIsInvited && eventHasClosed) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_CLOSED : STR_KEY.PROFILE_ALERT_CLOSED);// 'Registration for this event is now closed.';
    } else if (statusIsInvited && eventIsFull) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_FULL : STR_KEY.PROFILE_ALERT_FULL); // 'Sorry, this event is fully booked right now.';
    } else if (statusIsWaitlisted) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_WAITLISTED : STR_KEY.PROFILE_ALERT_WAITLISTED); //'You are currently on the waitlist for this event.';
    } else if (statusIsCancelled) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_CANCELLED : STR_KEY.PROFILE_ALERT_CANCELLED);//'You have declined to attend this event.';
    } else if (statusIsDidNotAttend) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_DID_NOT_ATTEND : STR_KEY.PROFILE_ALERT_DID_NOT_ATTEND);//'You did not attend this event.';
    } else if (statusIsRemoved) {
        showAlert = true;
        alertText = __(isSession ? STR_KEY.PROFILE_SESSION_ALERT_REMOVED : STR_KEY.PROFILE_ALERT_REMOVED);//'You have been removed from this event and can no longer attend, if you think this is a mistake please contact the organiser.';
    }

    if (showAlert) return renderAlert(alertText, alertType, alertIcon);

    /**
     * Show (checkin) button when event:
     * - has self checkin enabled 
     * AND
     * - is type online AND the attendance status is INVITED, REGISTERED, ATTENDED, ENGAGED
     * OR
     * - is type venue AND the attendance status is INVITED, REGISTERED
     */

    let showBtn;
    if (isCheckinEvent) {
        showBtn = checkinHasStarted && (onlineSingleEvent || onlineSessionEvent ? (statusCanAttend || statusIsAttending) : statusCanAttend);
    } else {
        showBtn = false;
    }

    if (showBtn) {
        let btnText      = RsvpService.isOnline(event) ? __(STR_KEY.PROFILE_BTN_ONLINE_CHECK_IN) : __(STR_KEY.PROFILE_BTN_VENUE_CHECK_IN);
        const btnIcon    = RSVP_CONSTS.ATTENDANCE.ICONS[event.attendance.status]
        const btnType    = GECKO_CONSTS.LABEL_TYPE_PRIMARY;
        const btnHandler = checkinHandler;

        return renderBtn(btnText, btnIcon, btnType, btnHandler);
    }

    /**
     * Show alert when the event:
     * - has self checkin enabled AND checkin has NOT started
     * OR
     * - is a venue event (with self checkin enabled) AND attendance status is ATTENDED or ENGAGED
     */

    if (isCheckinEvent && !checkinHasStarted) {
        showAlert    = true;
        const checkinTime = checkinTimeString(event, currentTime, startTime);
        alertText = `${RsvpService.isOnline(event) ? __(STR_KEY.PROFILE_ALERT_ONLINE_CHECK_IN_FROM) : __(STR_KEY.PROFILE_ALERT_VENUE_CHECK_IN_FROM)} ${checkinTime} (${eventTimezone})`
    } else if (venueEventWithCheckIn && statusIsAttending) {
        showAlert = true;
        alertText = __(STR_KEY.PROFILE_ALERT_CHECKED_IN);
        alertType = GECKO_CONSTS.ALERT_TYPE_SUCCESS;
        alertIcon = 'user-check'; 
    }

    if (showAlert) return renderAlert(alertText, alertType, alertIcon);

    return null;
}

export default StatusControl;
